/**
 * Navigation utilities for handling internal vs external URLs
 */

/**
 * Determines if a URL is internal (should use client-side routing) or external
 * @param url - The URL to check
 * @returns true if the URL is internal, false if external
 */
export const isInternalUrl = (url: string): boolean => {
  if (!url) return false;
  
  // Check for relative URLs (starting with /)
  if (url.startsWith('/')) return true;
  
  // Check for modal actions
  if (url.startsWith('modal:')) return true;
  
  // Check for hash/fragment URLs
  if (url.startsWith('#')) return true;
  
  // Check for query params only
  if (url.startsWith('?')) return true;
  
  // Check if URL matches current domain
  try {
    const urlObj = new URL(url);
    const currentDomain = window.location.origin;
    return urlObj.origin === currentDomain;
  } catch {
    // If URL parsing fails, treat as relative/internal
    return true;
  }
};

/**
 * Handles navigation for both internal and external URLs
 * @param url - The URL to navigate to
 * @param navigate - React Router navigate function
 * @param onModalAction - Optional callback for modal actions
 */
export const handleNavigation = (
  url: string,
  navigate: (path: string) => void,
  onModalAction?: (modalType: string) => void
): void => {
  if (!url) return;
  
  // Handle modal actions
  if (url.startsWith('modal:')) {
    const modalType = url.replace('modal:', '');
    if (onModalAction) {
      onModalAction(modalType);
    }
    return;
  }
  
  // Handle internal URLs with client-side routing
  if (isInternalUrl(url)) {
    navigate(url);
  } else {
    // Handle external URLs by opening in new tab
    window.open(url, '_blank', 'noopener,noreferrer');
  }
};